import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Editor from 'draft-js-plugins-editor'
import { RichUtils, DefaultDraftBlockRenderMap } from 'draft-js'
import { Map } from 'immutable'
import classnames from 'classnames/bind'
import StyleControls from './StyleControls'
import plugins, { modifiers, AlignmentTool, InlineToolbar } from './plugins'

import css from './styles.css'

const cx = classnames.bind(css)

// Custom overrides for inline styles
const defaultCustomStyleMap = {
  // TODO: define colors for text spans in here
}

// Maps the types of content blocks to CSS classes
const defaultBlockStyleFn = contentBlock => {
  switch (contentBlock.getType()) {
    case 'blockquote':
      return css.blockquote
    case 'colorblock':
      return css.colorblock
    case 'colorblock-header':
      return css['colorblock-header']
    case 'colorblock-footer':
      return css['colorblock-footer']
    default:
      return null
  }
}

// Defines supported block types & maps them to wrappers
const defaultBlockRenderMap = DefaultDraftBlockRenderMap.merge(
  Map({
    colorblock: {
      element: 'div',
    },
    'colorblock-header': {
      element: 'div',
    },
    'colorblock-footer': {
      element: 'div',
    },
  }),
)

const hidePlaceholder = contentState =>
  !contentState.hasText() &&
  contentState.getBlockMap().first().getType() !== 'unstyled'

const wrapInnerCss = (initialClassName, editorState, isBlastEmail) => {
  const contentState = editorState.getCurrentContent()

  return cx(initialClassName, {
    hidePlaceholder: hidePlaceholder(contentState),
    blastEmail: isBlastEmail,
    // must affect editor styles for our editor's *internal* DraftJS component,
    // which can't do css modules unfortunately.
    draftingBlastEmail: isBlastEmail,
  })
}

// define higher level components for rendering `ContentBlock` objects.
// by returning nothing, custom components are rendered as `DraftEditorBlock`
const defaultBlockRendererFn = () => null

class RichTextEditor extends Component {
  static propTypes = {
    messages: PropTypes.arrayOf(PropTypes.object).isRequired,
    plugins: PropTypes.array.isRequired,
    editorState: PropTypes.object.isRequired,
    hideImgPicker: PropTypes.bool,
    updateBodyState: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    showExtraButtons: PropTypes.bool,
    isBlastEmail: PropTypes.bool,
  }

  static defaultProps = {
    customStyleMap: defaultCustomStyleMap,
    blockStyleFn: defaultBlockStyleFn,
    blockRenderMap: defaultBlockRenderMap,
    blockRendererFn: defaultBlockRendererFn,
    showExtraButtons: false,
    isBlastEmail: false,
    hideImgPicker: false,
  }

  constructor(props) {
    super(props)

    this.plugins = this.props.plugins.concat(plugins)
  }

  handleKeyCommand = command => {
    const { editorState } = this.props
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      this.updateParentBodyState(newState)
      return true
    }
    return false
  }

  onTab = ev => {
    const maxDepth = 4
    this.updateParentBodyState(
      RichUtils.onTab(ev, this.props.editorState, maxDepth),
    )
  }

  toggleBlockType = blockType => {
    this.updateParentBodyState(
      RichUtils.toggleBlockType(this.props.editorState, blockType),
    )
  }

  toggleInlineStyle = inlineStyle => {
    this.updateParentBodyState(
      RichUtils.toggleInlineStyle(this.props.editorState, inlineStyle),
    )
  }

  updateParentBodyState = editorState => {
    this.props.updateBodyState(editorState)
  }

  setEditorRef = el => {
    this.editor = el
  }
  focusEditor = () => {
    this.editor.focus()
  }

  render() {
    const {
      messages,
      editorState,
      placeholder,
      showExtraButtons,
      handleTemplateSelection,
      hideImgPicker,
      isEmail,
      isBlastEmail,
      customStyleMap,
      blockRenderMap,
      blockStyleFn,
      blockRendererFn,
      openOptionsModal,
      openCreateTemplateModal,
      createModalOpen,
      optionsModalOpen,
      closeOptionsModal,
      templateSettings,
      isMessageSetting,
      practiceName
    } = this.props

    const outerWrapperCss = cx('outerWrapper', 'editor', {
      blastEmail: isBlastEmail,
    })
    const innerWrapperCss = wrapInnerCss(
      'innerWrapper',
      editorState,
      isBlastEmail,
    )
    return (
      <div className={outerWrapperCss}>
        <StyleControls
          messages={messages}
          openCreateTemplateModal={openCreateTemplateModal}
          createModalOpen={createModalOpen}
          editorState={editorState}
          toggleBlockType={this.toggleBlockType}
          toggleInlineStyle={this.toggleInlineStyle}
          updateParentBodyState={this.updateParentBodyState}
          modifier={modifiers.addImage}
          showExtraButtons={showExtraButtons}
          handleTemplateSelection={handleTemplateSelection}
          openOptionsModal={openOptionsModal}
          optionsModalOpen={optionsModalOpen}
          closeOptionsModal={closeOptionsModal}
          templateSettings={templateSettings}
          hideImgPicker={hideImgPicker}
          isMessageSetting={isMessageSetting}
          isEmail={isEmail}
          isBlastEmail={isBlastEmail}
          practiceName={practiceName}
        />
        <div className={innerWrapperCss} onClick={this.focusEditor}>
          <Editor
            customStyleMap={customStyleMap}
            blockStyleFn={blockStyleFn}
            blockRenderMap={blockRenderMap}
            blockRendererFn={blockRendererFn}
            editorState={editorState}
            handleKeyCommand={this.handleKeyCommand}
            onChange={this.updateParentBodyState}
            onTab={this.onTab}
            placeholder={placeholder}
            plugins={this.plugins}
            ref={this.setEditorRef}
            spellCheck
          />
          <AlignmentTool />
          <InlineToolbar />
        </div>
      </div>
    )
  }
}

export default RichTextEditor



// WEBPACK FOOTER //
// ./src/components/sitewide/RichTextEditor/index.js